import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SendRounded, ContentCopyRounded } from '@mui/icons-material';
import { surveyApi } from '../../../lib/api/survey.api';
import { errorText } from '../../../lib/error-text';

interface Props {
  tenantId: string;
  surveyId: string;
  title: string;
  onClose: () => void;
}

/** Admin diagnostics: why students can/can't see a survey + send a test email. */
export function SurveyDiagnosticsDialog({
  tenantId,
  surveyId,
  title,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const diagQ = useQuery({
    queryKey: ['survey-diagnostics', tenantId, surveyId],
    queryFn: () => surveyApi.diagnostics({ tenantId, surveyId }),
  });

  const testMutation = useMutation({
    mutationFn: () => surveyApi.sendTestEmail({ tenantId, surveyId, toEmail: email.trim() }),
    onSuccess: (r) =>
      setFeedback({ kind: 'success', msg: t('surveys.diag.testSent', { email: r.to }) }),
    onError: (e) =>
      setFeedback({
        kind: 'error',
        msg: errorText(e, t('surveys.diag.testError')),
      }),
  });

  const d = diagQ.data;

  const copyLink = () => {
    if (d?.answerUrl) void navigator.clipboard?.writeText(d.answerUrl);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('surveys.diag.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {title}
        </Typography>

        {diagQ.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : diagQ.isError || !d ? (
          <Alert severity="error">{t('surveys.diag.loadError')}</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color={d.acceptingNow ? 'success' : 'warning'}
                label={
                  d.acceptingNow
                    ? t('surveys.diag.acceptingYes')
                    : t(`surveys.diag.notAccepting.${d.reasonNotAccepting ?? 'closed'}`)
                }
              />
            </Stack>

            <Stack spacing={0.5}>
              <Row
                label={t('surveys.diag.status')}
                value={t(`surveys.status.${d.survey.status}`)}
              />
              <Row label={t('surveys.diag.opensAt')} value={d.survey.opensAt} />
              <Row
                label={t('surveys.diag.closesAt')}
                value={d.survey.closesAt ?? t('surveys.diag.noClose')}
              />
              <Row label={t('surveys.diag.eligible')} value={String(d.eligibleCount)} />
              <Row label={t('surveys.diag.responded')} value={String(d.respondedCount)} />
              <Row label={t('surveys.diag.pending')} value={String(d.pendingCount)} />
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('surveys.diag.answerUrl')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                  {d.answerUrl}
                </Typography>
                <Button size="small" startIcon={<ContentCopyRounded />} onClick={copyLink}>
                  {t('surveys.diag.copy')}
                </Button>
              </Stack>
            </Box>

            {d.siblingsInSection.length > 0 && (
              <Alert severity="warning">
                {t('surveys.diag.duplicatesWarn', { count: d.siblingsInSection.length })}
                <Stack sx={{ mt: 1 }} spacing={0.5}>
                  {d.siblingsInSection.map((s) => (
                    <Typography key={s.id} variant="caption">
                      • {s.title} — {t(`surveys.status.${s.status}`)}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('surveys.diag.testTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('surveys.diag.testHelp')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="flex-start">
                <TextField
                  size="small"
                  fullWidth
                  type="email"
                  placeholder={t('surveys.diag.testPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                  variant="contained"
                  startIcon={<SendRounded />}
                  disabled={!email.trim() || testMutation.isPending}
                  onClick={() => testMutation.mutate()}
                >
                  {testMutation.isPending ? t('surveys.diag.sending') : t('surveys.diag.send')}
                </Button>
              </Stack>
            </Box>

            {feedback && <Alert severity={feedback.kind}>{feedback.msg}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.close', { ns: 'common' })}</Button>
      </DialogActions>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}
